/**
 * InlineChoreStatus - Visual progress stepper for kiosk chore cards
 * Shows progress dots that kids can tap to mark chores complete
 * NO verification allowed - that's for parents on the main app only
 */
import React, { useState, useCallback } from 'react';
import { Check, Loader2 } from 'lucide-react';

interface InlineChoreStatusProps {
  status: string;
  verificationEnabled?: boolean;
  isParent?: boolean; // ignored - kiosk is always kid mode
  onStatusChange: (newStatus: string) => Promise<void>;
  disabled?: boolean;
}

// Step configuration
const STEPS = [
  { key: 'assigned', label: 'To Do', color: 'bg-blue-500', ring: 'ring-blue-400' },
  { key: 'completed', label: 'Done', color: 'bg-orange-500', ring: 'ring-orange-400' },
  { key: 'verified', label: 'Verified', color: 'bg-green-500', ring: 'ring-green-400' },
];

export const InlineChoreStatus: React.FC<InlineChoreStatusProps> = ({
  status,
  verificationEnabled = true,
  onStatusChange,
  disabled = false,
}) => {
  const [loading, setLoading] = useState(false);

  // Normalize status
  let normalizedStatus = status;
  if (status === 'pending_verification') {
    normalizedStatus = 'completed';
  } else if (status === 'pending' || status === 'assigned' || !status) {
    normalizedStatus = 'assigned';
  }

  const steps = verificationEnabled ? STEPS : STEPS.slice(0, 2);
  const currentIndex = steps.findIndex(s => s.key === normalizedStatus);
  const canComplete = normalizedStatus === 'assigned';

  const handleTap = useCallback(async () => {
    console.log('[InlineChoreStatus] handleTap called', { disabled, loading, canComplete, status, normalizedStatus });
    if (disabled || loading || !canComplete) {
      console.log('[InlineChoreStatus] Tap blocked:', { disabled, loading, canComplete });
      return;
    }

    console.log('[InlineChoreStatus] Starting status change to completed');
    setLoading(true);
    try {
      await onStatusChange('completed');
      console.log('[InlineChoreStatus] Status change succeeded');
    } catch (e) {
      console.error('[InlineChoreStatus] Failed:', e);
    } finally {
      setLoading(false);
    }
  }, [disabled, loading, canComplete, onStatusChange, status, normalizedStatus]);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    console.log('[InlineChoreStatus] handleTouchStart fired');
    // Don't preventDefault on touchstart - let the touch sequence complete
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    console.log('[InlineChoreStatus] handleTouchEnd fired', { target: (e.target as HTMLElement).className });
    e.preventDefault();
    e.stopPropagation();
    handleTap();
  }, [handleTap]);

  const handleClick = useCallback((e: React.MouseEvent) => {
    console.log('[InlineChoreStatus] handleClick fired');
    e.stopPropagation();
    handleTap();
  }, [handleTap]);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-16">
        <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div
      className={`flex items-center ${canComplete ? 'cursor-pointer active:opacity-70' : ''} min-h-[44px] min-w-[60px] px-2 -mx-2`}
      onClick={canComplete ? handleClick : undefined}
      onTouchStart={canComplete ? handleTouchStart : undefined}
      onTouchEnd={canComplete ? handleTouchEnd : undefined}
      style={{ touchAction: 'manipulation' }}
      role={canComplete ? 'button' : undefined}
      aria-label={canComplete ? 'Tap to mark complete' : 'Status indicator'}
      title={canComplete ? 'Tap to complete' : ''}
      data-has-touch={canComplete ? 'true' : undefined}
    >
      {steps.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isCurrent = index === currentIndex;
        const isNext = index === currentIndex + 1;
        const isActive = index <= currentIndex;

        return (
          <React.Fragment key={step.key}>
            {/* Connector line */}
            {index > 0 && (
              <div
                className={`w-2 h-0.5 ${isActive ? step.color : 'bg-gray-600'}`}
              />
            )}
            {/* Step dot */}
            <div
              className={`
                rounded-full flex items-center justify-center transition-all
                ${isCurrent ? 'w-5 h-5' : 'w-3 h-3'}
                ${isActive ? step.color : 'bg-gray-600'}
                ${isNext && canComplete ? `ring-2 ring-offset-1 ring-offset-gray-800 ${step.ring} animate-pulse` : ''}
              `}
            >
              {isCompleted && <Check className="w-2 h-2 text-white" strokeWidth={3} />}
              {isCurrent && index === steps.length - 1 && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default InlineChoreStatus;
